import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatDetail } from "@/lib/api";
import { CheckCircle2, Loader2, Send, MapPin, Users } from "lucide-react";

export default function SiteVisitPage() {
  const [search] = useSearchParams();
  const projectPrefill = search.get("project") || "";
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({
    project_id: projectPrefill,
    full_name: "", email: "", phone: "",
    party_size: 1, preferred: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(Array.isArray(data) ? data : data.projects || []);
      } catch {}
    })();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error("Please choose a project."); return; }
    if (!form.full_name || !form.email) { toast.error("Name and email are required."); return; }
    setBusy(true);
    try {
      await api.post("/site-visits/book", { ...form, party_size: Number(form.party_size) || 1 });
      setDone(true);
      toast.success("Site-visit request received.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <main data-testid="site-visit-page">
      <section className="mv-green-bg text-white py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Site visit</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[0.98]">
            Walk the land. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Meet our team.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/85 text-[15.5px] leading-relaxed">
            Book a guided site visit at any live MAVULA project. Bring your family, your lawyer, or your accountant — we'll show you the surveyed boundaries, the plans, and the paperwork.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 md:px-8 py-14 md:py-20">
        {done ? (
          <div className="mv-card p-8 flex items-start gap-3" data-testid="site-visit-success" style={{ background: "linear-gradient(180deg, #fff, rgba(37,211,102,0.06))" }}>
            <CheckCircle2 size={26} style={{ color: "#0A7A2C" }}/>
            <div>
              <div className="font-display text-2xl mv-green-text">Request received.</div>
              <p className="mt-2 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
                We'll confirm your visit date within <strong>1 working day</strong>. Bring an ID document and dress for outdoor site conditions.
              </p>
              <Link to="/projects" className="mt-4 inline-block btn-ghost" data-testid="site-visit-back">Browse more projects</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mv-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="site-visit-form">
            <label className="block md:col-span-2">
              <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Project *</span>
              <select value={form.project_id} onChange={set("project_id")} required
                      className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid="sv-project">
                <option value="">— Select a project —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.city}, {p.country}
                  </option>
                ))}
              </select>
            </label>
            <F label="Full name *"        v={form.full_name} on={set("full_name")} tid="sv-name"/>
            <F label="Email *"            v={form.email}     on={set("email")}     type="email" tid="sv-email"/>
            <F label="Phone / WhatsApp"   v={form.phone}     on={set("phone")}     tid="sv-phone"/>
            <label className="block">
              <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Party size</span>
              <input type="number" min={1} max={10} value={form.party_size} onChange={set("party_size")}
                     className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid="sv-party"/>
            </label>
            <F label="Preferred date"     v={form.preferred} on={set("preferred")} placeholder="e.g. 12 Aug 2026" className="md:col-span-2" tid="sv-date"/>
            <label className="block md:col-span-2">
              <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Anything we should know</span>
              <textarea value={form.message} onChange={set("message")} rows={3}
                        className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid="sv-message"/>
            </label>
            <div className="md:col-span-2 pt-3 border-t mv-hairline flex justify-between items-center">
              <div className="inline-flex items-center gap-2 text-[12.5px]" style={{ color: "var(--mv-muted)" }}>
                <MapPin size={14}/> Available at every live project · free of charge.
              </div>
              <button type="submit" disabled={busy} className="btn-green" data-testid="sv-submit">
                {busy ? <Loader2 className="animate-spin" size={16}/> : <Send size={16}/>}
                {busy ? "Sending…" : "Request site visit"}
              </button>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}

function F({ label, v, on, type = "text", placeholder, className = "", tid }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11.5px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>{label}</span>
      <input value={v} onChange={on} type={type} placeholder={placeholder}
             className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]" data-testid={tid}/>
    </label>
  );
}
